import type { ApplicationState, ChecklistItem } from "../../types/tracked-job";
import { isChecklistItem } from "../../types/tracked-job";

/**
 * Minimal job fields stored in DynamoDB user-state (matches prior `buildJobPayload` in useUserJobStateSync).
 */
export type JobLikeForUserState = {
  id: string;
  title?: unknown;
  normalisedTitle?: unknown;
  companyId?: unknown;
  companyName?: unknown;
  externalUrl?: string;
  applicationState?: ApplicationState;
  isHidden?: boolean;
  applicationDeadline?: unknown;
  notes?: unknown;
  tags?: unknown;
  checklist?: unknown;
};

/**
 * JSON-serializable map for PUT /api/user-state/* job keys. Keeps payloads small and avoids
 * sending entire Algolia hits (nested highlights, huge descriptions, etc.).
 */
export function buildJobUserStatePayload(
  job: JobLikeForUserState,
  extras: Record<string, unknown>
): Record<string, unknown> {
  return {
    id: job.id,
    objectID: job.id,
    title: job.title ?? null,
    normalisedTitle: job.normalisedTitle ?? null,
    companyId: job.companyId ?? null,
    companyName:
      typeof job.companyName === "string" && job.companyName.trim() ? job.companyName : null,
    ...(typeof job.externalUrl === "string" && job.externalUrl.length > 0
      ? { externalUrl: job.externalUrl }
      : {}),
    ...(job.applicationState !== undefined ? { applicationState: job.applicationState } : {}),
    ...(job.isHidden !== undefined ? { isHidden: job.isHidden } : {}),
    ...(typeof job.applicationDeadline === "string" && job.applicationDeadline.length > 0
      ? { applicationDeadline: job.applicationDeadline }
      : {}),
    ...(typeof job.notes === "string" ? { notes: job.notes } : {}),
    ...(Array.isArray(job.tags)
      ? { tags: job.tags.filter((t): t is string => typeof t === "string") }
      : {}),
    ...(Array.isArray(job.checklist)
      ? { checklist: job.checklist.filter((item): item is ChecklistItem => isChecklistItem(item)) }
      : {}),
    ...extras,
  };
}

export function buildSavedJobUserStatePayload(
  job: { id?: string; objectID?: string } & Record<string, unknown>,
  savedAt: string
): Record<string, unknown> {
  const id = String(job.id ?? job.objectID ?? "");
  return buildJobUserStatePayload(
    { ...job, id } as JobLikeForUserState,
    { savedAt }
  );
}
